import React, { useState, useRef, useEffect } from 'react';
import { useT } from '../i18n';
import './EmojiPicker.css';

// ── EMOJI PICKER ─────────────────────────────────────────────────────────────
// A button beside the message box that opens a small grid.
//
// DESKTOP ONLY, on purpose. Every phone and tablet keyboard already has an
// emoji key, so on touch this would be a second, worse picker occupying room
// the keyboard needs. It is hidden below the desktop breakpoint rather than
// rendered and shrunk — see EmojiPicker.css.
//
// A curated list rather than an emoji-picker package: the libraries ship the
// full Unicode set with search, skin-tone variants and their own data file,
// which is a lot of bundle for a chat between a customer and a decorator. These
// are the ones people actually send, grouped so the grid can be scanned.
//
// KEPT TO UNICODE 12 AND EARLIER. The newer interior-design emoji are exactly
// the ones you would want here — a window, a mirror, a ladder, a plank — but
// Unicode 13/14 has no glyph on Windows 10, where they render as empty boxes.
// Verified by drawing each one to a canvas and comparing it against a
// character with no glyph at all; anything that matched was replaced.

const GROUPS = [
  {
    id: 'smileys',
    icon: '🙂',
    emojis: ['😀', '😃', '😄', '😁', '😅', '😂', '🙂', '😉', '😊', '😍', '😘', '😋',
      '😎', '🤩', '🥳', '🤔', '🙃', '😌', '😔', '😢', '😭', '😤', '😱', '🤯',
      '😴', '🤗', '🤝', '🙏', '👍', '👎', '👏', '💪', '👋', '🤞', '✌️', '🙌'],
  },
  {
    id: 'home',
    icon: '🏠',
    emojis: ['🏠', '🏡', '🏢', '🛋️', '🛏️', '🚪', '🏘️', '🪑', '🚿', '🛁', '🚽', '🧹',
      '🧺', '🌱', '🌿', '🕯️', '💡', '🖼️', '🕰️', '📐', '📏', '🔨', '🔩', '🔧',
      '🧰', '🚧', '🎨', '🖌️', '🧱', '🌳', '🔑', '📦'],
  },
  {
    id: 'symbols',
    icon: '✅',
    emojis: ['✅', '❌', '⭐', '🌟', '🔥', '💯', '❤️', '🧡', '💛', '💚', '💙', '💜',
      '🎉', '🎊', '✨', '⚡', '⏰', '📅', '💰', '💸', '📞', '📧', '📎', '📌',
      '❗', '❓', '⚠️', '➡️'],
  },
];

export default function EmojiPicker({ onPick, disabled }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState(GROUPS[0].id);
  const wrapRef = useRef(null);

  // Close on an outside click or on Escape. Both, because a picker that only
  // closes one way is one someone gets stuck with.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = GROUPS.find(g => g.id === group) || GROUPS[0];

  return (
    <div className="ep-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ep-btn${open ? ' ep-btn--on' : ''}`}
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        aria-label={t('chat_emoji')}
        title={t('chat_emoji')}
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
          <circle cx="9" cy="9.5" r="0.6" fill="currentColor" />
          <circle cx="15" cy="9.5" r="0.6" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div className="ep-panel" role="dialog" aria-label={t('chat_emoji')}>
          <div className="ep-tabs">
            {GROUPS.map(g => (
              <button
                key={g.id}
                type="button"
                className={`ep-tab${g.id === group ? ' ep-tab--on' : ''}`}
                onClick={() => setGroup(g.id)}
                aria-pressed={g.id === group}
              >{g.icon}</button>
            ))}
          </div>

          <div className="ep-grid">
            {current.emojis.map(e => (
              <button
                key={e}
                type="button"
                className="ep-emoji"
                // The picker stays OPEN after a pick: people send several in a
                // row, and closing after each one makes that four clicks
                // instead of one.
                onClick={() => onPick(e)}
                aria-label={e}
              >{e}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
